import React, { useState, useCallback } from "react";
import {
  HeatMapComponent,
  Inject,
  Legend,
  Tooltip,
} from "@syncfusion/ej2-react-heatmap";

import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import FormGroup from "@material-ui/core/FormGroup";
import makeStyles from "@material-ui/core/styles/makeStyles";

import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import SearchIcon from "@material-ui/icons/Search";

import NotFound from "components/Texts/NotFound";
import UtilsFncs from "utils/UtilsFncs";
import FromToDateField from "components/_refactor/fields/from-to-date";

const useStyles = makeStyles({
  toggle: {
    textTransform: "capitalize",
  },
  selected: {
    fontWeight: "bold",
  },
});

const today = new Date();
const data7dias = new Date(today.setDate(today.getDate() - 8))
  .toISOString()
  .slice(0, 10);

const yesterday = new Date(today.setDate(new Date().getDate() - 1))
  .toISOString()
  .slice(0, 10);

const getHeatMapData = (heatMapdata) => {
  let higherVisit = 0;

  const dataSource = heatMapdata?.map((data) =>
    data.HorasValores?.map((info) => {
      if (info.Visitas > higherVisit) {
        higherVisit = info.Visitas;
      }

      return info.Visitas !== 0 ? info.Visitas : "";
    })
  );

  return {
    higherVisit,
    dataSource,
  };
};

const defaultPalette = [
  { value: 10, color: "#FFF99D" },
  { value: 20, color: "#FFF66F" },
  { value: 30, color: "#FEF025" },
  { value: 50, color: "#FFD462" },
  { value: 80, color: "#FFC733" },
  { value: 130, color: "#FF9333" },
  { value: 210, color: "#E15500" },
];

const getPalette = (higherVisit) => {
  if (higherVisit > 210) {
    const percentageToIncrease = ((higherVisit * 100) / 210 / 100).toFixed(2);

    const palette = defaultPalette.map((item) => ({
      ...item,
      value: parseInt((item.value * percentageToIncrease).toFixed(0), 10),
    }));

    return palette;
  }
  return defaultPalette;
};

const getHeatMapXLabels = (heatMapdata) => {
  return heatMapdata?.map(({ DataContagem }) =>
    UtilsFncs.ConvertDateFormat(DataContagem)
  );
};

const getHeatMapYLabels = (heatMapdata) => {
  return heatMapdata[0].HorasValores?.map(({ Hora }) => `${Hora}h`);
};

const tooltipTemplate = (args) => {
  args.content = [
    args.yLabel + " • " + args.xLabel + " • " + args.value + " Visitas",
  ];
};

export default function IndicatorsHeatMap({
  dados,
  pesquisar,
  searching,
  lastHeatMapParams,
  isMobile,
}) {
  const classes = useStyles();

  const [periodo, setPeriodo] = useState(
    lastHeatMapParams?.dtIni || lastHeatMapParams?.dtFim ? "periodo" : "mes"
  );
  const [dimensao, setDimensao] = useState(
    lastHeatMapParams?.dimensao || "visitas"
  );
  const [tipoPeriodo, setTipoPeriodo] = useState(
    lastHeatMapParams?.tipoPeriodo || "hora"
  );
  const [dataInicial, setDataInicial] = React.useState(
    lastHeatMapParams?.dtIni
      ? UtilsFncs.ConvertDateFormat(lastHeatMapParams.dtIni, "-", "/")
      : UtilsFncs.GetMoment(data7dias).format("DD/MM/YYYY")
  );
  const [dataFinal, setDataFinal] = React.useState(
    lastHeatMapParams?.dtFim
      ? UtilsFncs.ConvertDateFormat(lastHeatMapParams.dtFim, "-", "/")
      : UtilsFncs.GetMoment(yesterday).format("DD/MM/YYYY")
  );

  const handlePeriodo = useCallback(
    (event, novoPeriodo) => {
      if (novoPeriodo === "mes" || novoPeriodo !== periodo) {
        setDataInicial(UtilsFncs.GetMoment(data7dias).format("DD/MM/YYYY"));
        setDataFinal(UtilsFncs.GetMoment(yesterday).format("DD/MM/YYYY"));
      }

      setPeriodo(novoPeriodo);
    },
    [periodo]
  );

  const handleDimensao = useCallback((event, novaDimensao) => {
    setDimensao(novaDimensao);
  }, []);

  const handleTipoPeriodo = useCallback((event, novoTipoPeriodo) => {
    setTipoPeriodo(novoTipoPeriodo);
  }, []);

  const handleConsulta = useCallback(async () => {
    const isMonth = periodo === "mes";
    const date = new Date();

    const params = {
      isMonth,
      dtIni: isMonth
        ? new Date(date.getFullYear(), date.getMonth(), 1)
            .toISOString()
            .slice(0, 10)
        : UtilsFncs.ConvertDateFormat(dataInicial, "/", "-"),
      dtFim: isMonth
        ? new Date(date.getFullYear(), date.getMonth() + 1, 0)
            .toISOString()
            .slice(0, 10)
        : UtilsFncs.ConvertDateFormat(dataFinal, "/", "-"),
      dimensao,
      tipoPeriodo,
    };

    await pesquisar(params, true);
  }, [periodo, dataInicial, dataFinal, dimensao, tipoPeriodo, pesquisar]);

  const { higherVisit, dataSource } = getHeatMapData(dados);

  return (
    <>
      <Card>
        <CardHeader
          subheader={
            <Grid
              container
              component={Box}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs="auto">
                <Box
                  component={Typography}
                  variant="h2"
                  marginBottom="0!important"
                >
                  <Box component="span">Mapa de Calor - Visitas</Box>
                </Box>
              </Grid>
            </Grid>
          }
        />
        <CardContent>
          <Grid container>
            <Grid
              item
              xs={isMobile ? 12 : 6}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <FormGroup>
                <ToggleButtonGroup
                  value={periodo}
                  exclusive
                  onChange={handlePeriodo}
                  size="small"
                  aria-label="filtro de periodo de Evoluçao Lojas"
                >
                  <ToggleButton
                    value="mes"
                    aria-label="periodo mês"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Mês Atual
                  </ToggleButton>
                  <ToggleButton
                    value="periodo"
                    aria-label="periodo customizavel"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Período
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormGroup>
              {periodo === "periodo" && (
                <FromToDateField
                  label="Período"
                  disabled={searching}
                  initialDate={dataInicial}
                  finalDate={dataFinal}
                  setInitialDate={setDataInicial}
                  setFinalDate={setDataFinal}
                  isMobile={isMobile}
                />
              )}
            </Grid>
            <Grid
              item
              xs={isMobile ? 12 : 6}
              style={{
                display: "flex",
                justifyContent: isMobile ? "flex-start" : "flex-end",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "flex-start",
              }}
            >
              <FormGroup>
                {/* <ToggleButtonGroup
                  value={dimensao}
                  exclusive
                  onChange={handleDimensao}
                  size="small"
                  aria-label="filtro de dimensão do mapa de calor"
                >
                  <ToggleButton
                    value="visitas"
                    aria-label="dimensão visitas do mapa de calor"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Visitas
                  </ToggleButton>
                </ToggleButtonGroup> */}
                <ToggleButtonGroup
                  value={tipoPeriodo}
                  exclusive
                  onChange={handleTipoPeriodo}
                  size="small"
                  aria-label="filtro de tipo de período do mapa de calor - visitas"
                >
                  <ToggleButton
                    value="hora"
                    aria-label="tipo de período de hora do mapa de calor - visitas"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Dia
                  </ToggleButton>
                  <ToggleButton
                    value="diasemana"
                    aria-label="tipo de período de dia da semana do mapa de calor - visitas"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Dia Semana
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormGroup>
              <Button
                variant="contained"
                onClick={handleConsulta}
                disabled={searching}
                startIcon={<SearchIcon style={{ width: 18, height: 18 }} />}
                style={{ display: "flex", marginLeft: isMobile ? "0" : "1rem" }}
              >
                {searching ? "Consultando..." : "Consultar"}
              </Button>
            </Grid>
            <Grid item xs={12} style={{ marginTop: isMobile ? "1rem" : 0 }}>
              {dados.length > 0 ? (
                <Box position="relative" height="350px">
                  <HeatMapComponent
                    id="heatmap"
                    height={"350px"}
                    renderingMode={"SVG"}
                    showTooltip={true}
                    dataSource={dataSource}
                    tooltipRender={tooltipTemplate}
                    xAxis={{
                      labels: getHeatMapXLabels(dados),
                    }}
                    yAxis={{
                      labels: getHeatMapYLabels(dados),
                    }}
                    paletteSettings={{
                      emptyPointColor: "#ffffff",
                      palette: getPalette(higherVisit),
                      type: "Gradient",
                    }}
                  >
                    <Inject services={[Legend, Tooltip]} />
                  </HeatMapComponent>
                </Box>
              ) : (
                <NotFound title="Não foram encontrados dados para Mapa de Calor." />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}
